import React from "react";
import { Grid, Typography, Box, Alert, TextField, Chip } from "@mui/material";
import type { EntitlementConfiguration, IntOrUnlimited, IntOrUnlimitedWithCountDisabledUnits } from "client/api-v2";
import { ENTITLEMENT_FIELD_ORDER } from "../../constants/entitlement-field-order";
import { formatInclusionName, formatEntitlementValue } from "../../utils/plan-utils";

interface OverridesDisplayProps {
  overrides?: EntitlementConfiguration;
}

export function OverridesDisplay({ overrides }: OverridesDisplayProps) {
  if (!overrides) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Entitlement Overrides
        </Typography>
        <Alert severity="info">
          No entitlement overrides are configured for this subscription. Use the{" "}
          <strong>"Edit Entitlement Overrides"</strong> button on the subscription details page to customize
          entitlements.
        </Alert>
      </Box>
    );
  }

  // Get all override fields that have values
  const overrideEntries = Object.entries(overrides)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => ({ key, value }));

  // Sort by field order
  const fieldOrder: readonly string[] = ENTITLEMENT_FIELD_ORDER;
  const sortedOverrides = overrideEntries.sort((a, b) => {
    const indexA = fieldOrder.indexOf(a.key);
    const indexB = fieldOrder.indexOf(b.key);

    if (indexA === -1 && indexB === -1) return a.key.localeCompare(b.key);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  if (sortedOverrides.length === 0) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Entitlement Overrides
        </Typography>
        <Alert severity="info">
          No entitlement overrides are configured for this subscription. Use the{" "}
          <strong>"Edit Entitlement Overrides"</strong> button on the subscription details page to customize
          entitlements.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography variant="h6">Entitlement Overrides</Typography>
        <Chip label={`${sortedOverrides.length} active`} size="small" color="primary" />
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        These are custom entitlement values that override the plan's base entitlements. Use the{" "}
        <strong>"Edit Entitlement Overrides"</strong> button on the subscription details page to modify these values.
      </Alert>
      <Grid container spacing={2}>
        {sortedOverrides.map(({ key, value }) => {
          // value can be any EntitlementConfiguration property value
          const entitlementValue:
            | IntOrUnlimitedWithCountDisabledUnits
            | IntOrUnlimited
            | boolean
            | number
            | null
            | undefined = value;
          const formattedValue = formatEntitlementValue(entitlementValue);
          const isBooleanField = typeof value === "boolean";
          const gridSize = isBooleanField ? 12 : 6;

          return (
            <Grid
              key={key}
              size={{
                xs: 12,
                sm: gridSize,
              }}
            >
              <TextField
                fullWidth
                label={formatInclusionName(key)}
                value={formattedValue}
                disabled
                size="small"
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
